// Determine where a changelog visit came from. Two signals, in order:
//   1. ?utm_source=... in the URL — set by us when we generate links we
//      want to attribute (What's New dialog, related-release cross-links,
//      future push/email/Slack announcements).
//   2. document.referrer — best-effort fallback.
//
// IMPORTANT — what we can actually detect in practice
// ----------------------------------------------------
// UTM is the only reliable signal. Most cross-origin referrers from email
// clients (Gmail/Outlook/Yahoo/Proton), Slack, and Teams arrive *empty*
// because those services send `Referrer-Policy: strict-origin` or
// `no-referrer`, AND Slack/Outlook safelinks redirect through their own
// domains which themselves strip the referrer. The REFERRER_HOST_MAP
// below is mostly aspirational — it catches in-browser web clients (e.g.
// mail.google.com in a tab still leaks origin under default policy) but
// will silently miss native mail apps, the Slack desktop app, etc.
//
// So in the analytics page, expect: high 'direct' share for off-platform
// links unless we UTM-stamp them; UTM tags for everything we control.
//
// API split:
//   readChangelogSource()       — PURE: read URL + referrer, no side effects.
//                                 Safe to call multiple times (StrictMode).
//   consumeChangelogUtmSource() — IMPURE: strip ?utm_source= from the URL
//                                 via history.replaceState so a reload
//                                 doesn't re-attribute the same visit.
//                                 Call exactly once per real visit.

const SOURCE_RE = /^[a-z][a-z0-9_]{0,31}$/

const REFERRER_HOST_MAP: Array<[RegExp, string]> = [
  [/(^|\.)mail\.google\.com$/, 'email'],
  [/(^|\.)outlook\.(live|office|office365)\.com$/, 'email'],
  [/(^|\.)mail\.yahoo\.com$/, 'email'],
  [/(^|\.)mail\.proton\.me$/, 'email'],
  [/(^|\.)slack\.com$/, 'slack'],
  [/(^|\.)slack-redir\.net$/, 'slack'],
  [/(^|\.)teams\.microsoft\.com$/, 'teams'],
  [/(^|\.)web\.telegram\.org$/, 'telegram'],
  [/(^|\.)t\.me$/, 'telegram'],
]

function readUtmSourceRaw(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = new URL(window.location.href).searchParams.get('utm_source')
    if (!raw || !SOURCE_RE.test(raw)) return null
    return raw
  } catch {
    return null
  }
}

function classifyReferrer(referrer: string): string {
  try {
    const ref = new URL(referrer)
    if (typeof window !== 'undefined' && ref.origin === window.location.origin) {
      const path = ref.pathname
      if (path.startsWith('/changelog')) return 'internal_changelog'
      if (path.startsWith('/guide')) return 'internal_guide'
      if (path === '/' || path === '') return 'internal_home'
      if (path.startsWith('/apps')) return 'internal_apps'
      return 'internal_other'
    }
    for (const [host, tag] of REFERRER_HOST_MAP) {
      if (host.test(ref.hostname)) return tag
    }
    return 'external_other'
  } catch {
    return 'unknown'
  }
}

export interface ChangelogSourceSignals {
  source: string
  referrer: string | null
}

/**
 * Pure read: no DOM/URL mutation. Idempotent — safe to call from a useEffect
 * that may double-run in React StrictMode.
 */
export function readChangelogSource(): ChangelogSourceSignals {
  const referrer = typeof document !== 'undefined' ? document.referrer || null : null
  const utm = readUtmSourceRaw()
  if (utm) return { source: utm, referrer }
  if (referrer) return { source: classifyReferrer(referrer), referrer }
  return { source: 'direct', referrer: null }
}

/**
 * Strip ?utm_source= from the current URL via history.replaceState so a
 * reload doesn't re-attribute the same visit. Side-effecting — callers must
 * latch this to fire exactly once per visit (e.g. via useRef), otherwise
 * StrictMode double-mounts will read a different source on the second pass.
 */
export function consumeChangelogUtmSource(): void {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('utm_source')) return
    url.searchParams.delete('utm_source')
    const newSearch = url.searchParams.toString()
    const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`
    window.history.replaceState({}, '', newUrl)
  } catch {
    // URL parse failure — leave it alone.
  }
}
